/* Copyright (c) 2016-2023 Planticle Pty Ltd */
"use strict";

import ko from "knockout";
import birdata from "../../api.js";
import _ from "underscore";
import SortableTableViewModel from "../shared/sortable-table.js";
import util from '../../util/util.js';
import moment from "dayjs";

// Wraps a function fn, and forwards all arguments to that function plus an additional 'isStale' argument
// This isStale argument is a function that returns true if the wrapped function was called
function preventStale(fn) {
	function wrapper() {
		var token = {};
		wrapper.token = token;

		function isStale() {
			return token !== wrapper.token;
		}

		var args = [].slice.call(arguments);
		args.unshift(isStale);
		fn.apply(this, args);
	}
	return wrapper;
}

function ManageSurveysViewModel(params) {
	var self = this;

	self.searchHasFocus = ko.observable(true); // Immediately focus search field

	self.searchText = ko.observable('').extend({ rateLimit: 400 });

	self.surveys = ko.observable([]);
	self.surveysLoaded = ko.observable(true);
	self.surveysError = ko.observable(false);

	self.selectedSurvey = ko.observable();

	self.programs = ko.observable([]);
	birdata.lookup('program').then(self.programs);

	self.programFilter = ko.observable('');
	self.afterDateFilter = ko.observable();
	self.beforeDateFilter = ko.observable();

	self.s2020UserFilter = ko.observable('').extend({ rateLimit: 400 });
	self.s2020SurveyFilter = ko.observable('').extend({ rateLimit: 400 });
	self.s2020PartOfBiannualCount = ko.observable(null);

	self.userFilter = ko.observable(null);
	self.usersMatchingSearch = ko.observable([]);
	self.usersSearchText = ko.observable('').extend({ rateLimit: 400 });

	self.usersSearchText.subscribe(function(search) {
		updateUsers(search, self.usersSearchText, self.usersMatchingSearch);
	});

	// Include layer in area name for drop down menu
	self.areasAndRegions = ko.observable([]);
	Promise.all([
		birdata.areas(),
		birdata.areaLayers({ include_hidden: true }),
		birdata.regions()
	]).then(function([rawAreas, layers, rawRegions]) {
		var areas = _.map(rawAreas, function(area) {
			var layer = _.findWhere(layers, {id: area.layer_id});
			return {
				id: area.id,
				title: area.name + " (" + layer.name + ")",
				type: 'area'
			};
		});
		var regions = _.map(rawRegions, function(region) {
			return {
				id: region.id,
				title: region.name + " (Regional Group)",
				type: 'region'
			};
		});

		self.areasAndRegions(areas.concat(regions));
	}).catch(function(error) {
		console.log(error);
	});

	self.areaOrRegionFilter = ko.observable();

	function updateUsers(search, searchObs, matchesObs) {
		if(search) {
			birdata.users({q:search, full: true}).then(function(users) {
				if(searchObs() != search) return; // ignore stale request
				matchesObs(users);
			});
		} else {
			matchesObs([]);
		}
	}

	self.userOptionText = function(user) {
		return _.filter([user.full_name, user.email, user.raisers_edge_id, user.username], _.identity).join(", ");
	};

	self.searchCriteria = ko.pureComputed(function() {
		var criteria = {};

		if(self.afterDateFilter()) {
			criteria.from = moment(self.afterDateFilter()).format("YYYY-MM-DD");
		}

		if(self.beforeDateFilter()) {
			criteria.to = moment(self.beforeDateFilter()).format("YYYY-MM-DD");
		}

		if(self.programFilter()) {
			criteria.program = self.programFilter().code;
		}

		if(self.searchText()) {
			criteria.q = self.searchText();
		}

		if(self.userFilter()) {
			criteria.user_id = self.userFilter().id;
		}

		if(self.s2020UserFilter()) {
			criteria.s2020_user_id = self.s2020UserFilter();
		}

		if(self.s2020SurveyFilter()) {
			criteria.source_ref = self.s2020SurveyFilter();
			criteria.source = 's2020';
		}

		if(self.s2020PartOfBiannualCount() != null) {
			criteria.s2020_part_of_biannual_count = (self.s2020PartOfBiannualCount() == 'Yes');
		}

		var areaOrRegion = self.areaOrRegionFilter();
		if(areaOrRegion) {
			if(areaOrRegion.type == 'area') {
				criteria.area_id = areaOrRegion.id;
			}
			if(areaOrRegion.type == 'region') {
				criteria.region_id = areaOrRegion.id;
			}
		}

		return criteria;
	});

	var updateWithCriteria = preventStale(function(isStale, criteria) {
		console.log(criteria);
		if(!_.isEmpty(criteria)) {
			criteria._options = { headers: { 'Cache-Control': 'no-cache' } };
			criteria.limit = 500;
			criteria.excluded_program = '';
			criteria.completed = 'any';
			self.surveysLoaded(false);
			self.surveysError(false);
			birdata.surveys(criteria).then(function(surveys) {
				if(!isStale()) {
					self.surveys(surveys);
					self.surveysLoaded(true);
				} else {
					console.log("Ignoring stale response");
				}
			}).catch(function(error) {
				console.log("error");
				console.log(error);
				if(!isStale()) {
					self.surveys([]);
					if(error.xhr.status == 401 || error.xhr.status == 403) {
						self.surveysError('Permission denied');
					} else {
						self.surveysError('Failed to load surveys');
					}
				}
			});
		} else {
			return self.surveys([]);
		}
	});

	self.searchCriteria.subscribe(updateWithCriteria);

	var userCache = {};
	function lookupUser(id) {
		if(!(id in userCache)) {
			userCache[id] = ko.observable();
			birdata.user(id).then(userCache[id]);
		}
		return userCache[id];
	}

	var sources = ko.observable([]);
	birdata.lookup('source').then(sources);

	var surveyTypes = ko.observable([]);
	birdata.lookup('survey_type').then(surveyTypes);

	self.selectedSurveyIDs = ko.observableArray([]);

	self.toggleSelectAll = function() {
		self.allSelected(!self.allSelected());
	};

	self.allSelected = ko.pureComputed({
		read: function() {
			return self.surveys().length > 0 && self.selectedSurveyIDs().sort().join(",") == _.pluck(self.surveys(), 'id').sort().join(",");
		},
		write: function(value) {
			console.log(value);
			if(value) {
				self.selectedSurveyIDs(_.pluck(self.surveys(), 'id'));
			} else {
				self.selectedSurveyIDs.removeAll();
			}
		}
	});

	self.anySelected = ko.pureComputed(function() {
		return self.selectedSurveyIDs().length > 0;
	});

	self.surveyCountText = ko.pureComputed(function() {
		var n = self.surveys().length;
		if(n == 0) {
			return "";
		} if(n == 1) {
			return "1 survey found";
		} if(n < 500) {
			return n + " surveys found";
		} else {
			return "More than 500 surveys found (only showing first 500, please refine your search)";
		}
	});

	self.surveyTable = new SortableTableViewModel([
		{
			name: '',
			sortable: false,
			value: function() {},
			html: function(survey) { return "<input type='checkbox' " + (self.selectedSurveyIDs.indexOf(survey.id) == -1 ? "" : "checked") + ">"; },
			click: function(survey, e) {
				e.stopPropagation();
				if(self.selectedSurveyIDs.remove(survey.id).length == 0) {
					self.selectedSurveyIDs.push(survey.id);
				}
				console.log(self.selectedSurveyIDs());
				return true;
			},
			id: 'check'
		},
		{
			name: 'Survey ID',
			value: function(survey) { return survey.id; },
			html: function(survey) { return this.value(survey); },
			id: 'survey_id'
		}, {
			name: 'Observer',
			value: function(survey) {
				return survey.observer_name;
			},
			html: function(survey) { return this.value(survey); },
			id: 'observer'
		}, {
			name: 'Survey Point',
			value: function(survey) { return survey.survey_point.name; },
			html: function(survey) { return this.value(survey); },
			id: 'survey_point'
		}, {
			name: 'Source',
			value: function(survey) {
				var source = _.findWhere(sources(), { code: survey.source});
				return source ? source.name : survey.source;
			},
			html: function(survey) { return this.value(survey); },
			id: 'source'
		}, {
			name: 'Date',
			value: function(survey) {
				return survey.start_date;
			},
			formatted: function(survey) {
				var val = this.value(survey);
				return val ? moment(val).format("DD/MM/YYYY") : "–";
			},
			id: 'start_date'
		}, {
			name: 'Survey type',
			value: function(survey) {
				var type = _.findWhere(surveyTypes(), { code: survey.survey_type });
				if(type) return type.name;
			},
			formatted: function(s) {
				return this.value(s);
			},
			id: 'survey_type'
		}, {
			name: 'Notes',
			value: function(survey) {
				return survey.notes || "";
			},
			html: function(s) {
				var notes = this.value(s);
				var maxChars = 60;

				if(notes.length > maxChars) {
					return [
						util.escapeHtml(notes.substr(0, maxChars)) + "&hellip;",
						"<div class='help'>",
						"<span class='icon'></span>",
						"<span class='text right top'>" + util.escapeHtml(notes) + "</span>",
						"</div>"
					].join(" ");
				} else {
					return util.escapeHtml(notes);
				}
			},
			id: 'notes'
		}, {
			name: 'Sp/Total',
			value: s => s.species_count,
			formatted: s => util.formatNumber(s.species_count) + "/" + util.formatNumber(s.total_count)
		}
	]);

	self.clickSurvey = function(survey) {
		self.selectedSurvey(survey);
		// load full survey
		birdata.survey(survey.id).then(function(fullSurvey) {
			if(self.selectedSurvey() == survey) {
				self.selectedSurvey(fullSurvey);
			}
		});
	};

	self.closeSurvey = function() {
		self.selectedSurvey(null);
	};

	self.afterDelete = function() {
		self.selectedSurvey(null);
		self.refresh();
	}

	self.deleteInProgress = ko.observable(false);

	self.refresh = function() {
		updateWithCriteria(self.searchCriteria());
	};

	self.sortedSurveys = self.surveyTable.sortedItems(self.surveys);

	self.noResults = ko.pureComputed(function() {
		return self.surveysLoaded() && self.surveys().length == 0 && !_.isEmpty(self.searchCriteria());
	});

	self.resultCount = ko.pureComputed(function() {
		if(self.surveysLoaded() && !_.isEmpty(self.searchCriteria())) {
			var len = self.surveys().length;
			if(len == 500) {
				return "Only showing first 500 surveys";
			} else if(len > 0) {
				return util.formatQuantity(len, "survey");
			}
		} else {
			return "";
		}
	});

	// -- Reallocate users

	self.showReallocateDialog = ko.observable(false);

	self.reallocateSearchText = ko.observable('').extend({ rateLimit: 400 });
	self.reallocateUsersMatchingSearch = ko.observable([]);
	self.reallocateSelectedUser = ko.observable(null);

	self.reallocateSearchText.subscribe(function(search) {
		updateUsers(search, self.reallocateSearchText, self.reallocateUsersMatchingSearch);
	});

	self.reallocateSurveyButtonTitle = ko.pureComputed(function() {
		var n = self.selectedSurveyIDs().length;
		return n > 1 ? ("Reallocate " + n + " surveys") : "Reallocate survey";
	});

	self.openReallocateDialog = function() {
		self.reallocateSearchText('');
		self.reallocateSelectedUser(null);
		self.showReallocateDialog(true);
	};

	self.closeReallocateDialog = function() {
		self.showReallocateDialog(false);
	};

	self.performReallocate = function() {
		birdata.reallocateSurveys(self.selectedSurveyIDs(), self.reallocateSelectedUser().id)
		.then(function() {
			self.closeReallocateDialog();
			self.selectedSurveyIDs.removeAll();
			self.refresh();
		})
		.catch(function(e) {
			console.log(e);
			alert("Reallocating surveys failed");
		});
	};

	self.assignBiannualCount = function() {
		birdata.bulkSurveyUpdate(self.selectedSurveyIDs(), { s2020_part_of_biannual_count: true })
		.then(function() {
			self.refresh();
		})
		.catch(e => {
			alert("Failed to update surveys");
			throw e;
		})
	}
}

ko.components.register('manage-surveys', {
	viewModel: function(params) {
		return new ManageSurveysViewModel(params);
	},
	template: require('../../../templates/admin/manage-surveys.html')
});
